import { useFirestore } from 'react-redux-firebase';
import { useCallback, useEffect, useState } from 'react';

export const useProfiles = () => {
  const firestore = useFirestore();
  const [reload, setReload] = useState(false);
  const [queryProfiles, setQueryProfiles] = useState({ field: null, condition: null, value: null });
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [selectProfiles, setSelectProfiles] = useState([]);

  const loadProfiles = useCallback(
    (field, condition, value) => {
      let ref = firestore.collection('profiles');
      if (field && condition && value) {
        ref = ref.where(field, condition, value);
      }
      ref
        .orderBy('email', 'asc')
        .get()
        .then((querySnapshot) => {
          const profiles = [];
          querySnapshot.forEach((doc) => {
            profiles.push({ id: doc.id, ...doc.data() });
          });
          setProfiles(profiles);
          setFilteredProfiles(profiles);
          setReload(false);
          setQueryProfiles({ field, condition, value });
        });
    },
    [firestore],
  );

  useEffect(() => {
    if (reload) {
      loadProfiles(queryProfiles.field, queryProfiles.condition, queryProfiles.value);
    }
  }, [reload, loadProfiles, queryProfiles]);

  const loadProfileSignedUp = useCallback(() => {
    loadProfiles('signUpStatus', '==', 'SIGNED_UP');
  }, [loadProfiles]);

  const filterProfiles = useCallback(
    (filterBy) => {
      if (profiles) {
        let filterProfiles = profiles.filter((profile) => {
          return filterBy(profile);
        });
        setFilteredProfiles(filterProfiles);
      }
    },
    [profiles],
  );

  const filterProfilesByCountry = useCallback(
    (country) => {
      filterProfiles((profile) => profile.countryCode === country);
    },
    [filterProfiles],
  );

  const filterProfilesByCountryAndState = useCallback(
    (country, state) => {
      filterProfiles((profile) => profile.countryCode === country && profile.stateCode === state);
    },
    [filterProfiles],
  );

  const filterProfileByEmailAndName = useCallback(
    (searchTerm, country) => {
      if (searchTerm.length > 0) {
        filterProfiles(
          (profile) =>
            (profile.countryCode === country && profile.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (profile.countryCode === country && profile.email.toLowerCase().includes(searchTerm.toLowerCase())),
        );
      } else {
        filterProfilesByCountry(country);
      }
    },
    [filterProfiles, filterProfilesByCountry],
  );

  const selectProfile = useCallback(
    (profile) => {
      if (selectProfiles.includes(profile)) {
        const sProfiles = selectProfiles.filter((sp) => sp.id !== profile.id);
        setSelectProfiles(sProfiles);
      } else {
        const sProfiles = selectProfiles;
        sProfiles.push(profile);
        setSelectProfiles(sProfiles);
      }
    },
    [selectProfiles],
  );

  const resetFilterProfiles = useCallback(() => {
    setFilteredProfiles(profiles);
    setSelectProfiles([]);
  }, [profiles]);

  const cleanFilters = useCallback(() => {
    setFilteredProfiles([]);
    setSelectProfiles([]);
  }, []);

  const cleanSelectProfiles = useCallback(() => {
    setSelectProfiles([]);
  }, []);

  const reloadProfiles = useCallback(() => {
    setReload(true);
  }, []);

  const getFilteredProfiles = useCallback(() => {
    if (selectProfiles.length > 0) {
      return selectProfiles;
    } else {
      return filteredProfiles;
    }
  }, [filteredProfiles, selectProfiles]);

  return {
    filteredProfiles,
    selectProfiles,
    selectProfile,
    filterProfilesByCountry,
    filterProfilesByCountryAndState,
    filterProfileByEmailAndName,
    resetFilterProfiles,
    cleanFilters,
    cleanSelectProfiles,
    loadProfileSignedUp,
    getFilteredProfiles,
    reloadProfiles,
  };
};
